import { useEffect, useState } from 'react';
import { apiUpdatePaymentsSettings, type PaymentsSettings } from '~/lib/api/payments';
import { classNames } from '~/utils/classNames';
import { apiErrorMessage, isValidCurrency } from './payments-model';
import { BTN_SECONDARY, CARD, ErrorBanner, Field, Section, Spinner } from './ui';

export function SettingsSection({
  appId,
  settings,
  onSaved,
}: {
  appId: string;
  settings: PaymentsSettings;
  onSaved: () => void;
}) {
  const [currency, setCurrency] = useState(settings.currency);
  const [email, setEmail] = useState(settings.notifyEmail ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const isLive = settings.liveState === 'live';

  // A reload (after going live, connecting an account…) resets the form to the server's values.
  useEffect(() => {
    setCurrency(settings.currency);
    setEmail(settings.notifyEmail ?? '');
  }, [settings.currency, settings.notifyEmail]);

  const nextCurrency = currency.trim().toUpperCase();
  const nextEmail = email.trim();
  const dirty = nextCurrency !== settings.currency || nextEmail !== (settings.notifyEmail ?? '');
  const valid = isValidCurrency(nextCurrency) && (!nextEmail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!dirty || !valid) {
      return;
    }

    setSaving(true);
    setError(null);
    setSaved(false);

    try {
      await apiUpdatePaymentsSettings(appId, {
        ...(nextCurrency !== settings.currency ? { currency: nextCurrency } : {}),
        notifyEmail: nextEmail || null,
      });
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save your settings.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Settings" icon="i-ph:sliders-horizontal">
      <form onSubmit={save} className={classNames(CARD, 'space-y-3 p-4')}>
        <Field
          label="Currency"
          hint={
            isLive
              ? 'Pause live payments to change currency — it decides which provider takes the payment.'
              : 'Three-letter code, e.g. INR, USD, EUR. INR uses Razorpay; other currencies use Stripe.'
          }
        >
          <input
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
            disabled={isLive}
            maxLength={3}
            spellCheck={false}
            className="ui-input-mono w-24 uppercase disabled:opacity-50"
          />
        </Field>

        <Field label="Payment notifications" hint="Where payment notifications are sent. Needed before you go live.">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="ui-input"
          />
        </Field>

        {error && <ErrorBanner message={error} />}

        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving || !dirty || !valid} className={BTN_SECONDARY}>
            {saving && <Spinner />}
            {saving ? 'Saving…' : 'Save settings'}
          </button>
          {saved && !dirty && <span className="text-[11px] text-muted">Saved</span>}
        </div>
      </form>
    </Section>
  );
}
